import { useState } from 'react';
import { Search, FileDown, PackageSearch, Factory, Truck, ArrowRightLeft, ShieldCheck } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import EstadoVacio from '../ui/EstadoVacio';
import Motivo from '../experiencia/Motivo';
import NotaExplicativa from '../ui/NotaExplicativa';

// RASTREAR UN LOTE · Módulo de Calidad, Fase 1.
//
// UNA SOLA CAJA, Y EL SISTEMA ADIVINA. Quien abre esta pantalla acaba de
// recibir una llamada —«el número de parte 4471-B falló en la línea»— y no
// tiene por qué saber si lo que le dieron es un lote nuestro, uno del
// proveedor, un folio de embarque o el número con el que el cliente llama
// al producto. Pedirle que elija el tipo de búsqueda antes de buscar es
// pedirle que traduzca antes de preguntar.
//
// Y SE DICE CÓMO SE INTERPRETÓ, siempre. Si acertamos mal, quien mira lo
// ve en una frase en vez de creerse un resultado que no era el suyo.

const fmt = new Intl.DateTimeFormat('es-MX', {
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit',
});
const soloFecha = new Intl.DateTimeFormat('es-MX', {
  day: '2-digit', month: '2-digit', year: 'numeric',
});

const cuando = (v) => (v ? fmt.format(new Date(v)) : '—');
const dia = (v) => (v ? soloFecha.format(new Date(v)) : '—');

// Lo que hace falta un periodo para poder buscar. Se dice ANTES de que la
// persona se quede mirando una lista vacía preguntándose qué hizo mal.
const NECESITAN_PERIODO = ['NUMERO_PARTE_CLIENTE', 'SKU'];

export default function Rastrear() {
  const { puede } = useAuth();
  const [consulta, setConsulta] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [reporte, setReporte] = useState(null);

  const puedeReportar = puede ? puede('trazabilidad.reporte') : false;
  const rangoIncompleto = Boolean(desde) !== Boolean(hasta);

  async function buscar(e) {
    e?.preventDefault();
    if (!consulta.trim() || rangoIncompleto) return;
    setCargando(true);
    setError(null);
    setReporte(null);
    try {
      const q = new URLSearchParams({ q: consulta.trim() });
      if (desde && hasta) { q.set('desde', desde); q.set('hasta', hasta); }
      setDatos(await api.get(`/trazabilidad/rastrear?${q}`));
    } catch (err) {
      setError(err.message);
      setDatos(null);
    } finally {
      setCargando(false);
    }
  }

  async function generar() {
    setCargando(true);
    setError(null);
    try {
      setReporte(await api.post('/trazabilidad/reporte', {
        consulta: consulta.trim(),
        desde: desde || null,
        hasta: hasta || null,
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Rastrear un lote</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          De qué proveedor vino y a qué cliente salió, con fechas, cantidades y quién lo movió.
        </p>
      </div>

      <NotaExplicativa titulo="Escribe lo que tengas a la mano">
        Acepta <strong>el lote nuestro</strong>, <strong>el lote del proveedor</strong>,
        {' '}<strong>el folio de un embarque</strong> o <strong>el número de parte del cliente</strong>.
        No hace falta que elijas cuál es: TEGO lo averigua y te dice cómo lo entendió.
        Para buscar por número de parte o por código de producto sí hace falta un periodo;
        sin él salen todos los lotes de su historia.
      </NotaExplicativa>

      <form onSubmit={buscar} className="space-y-3">
        <div className="flex gap-2">
          <input
            value={consulta}
            onChange={(e) => setConsulta(e.target.value)}
            placeholder="Lote, lote del proveedor, embarque o número de parte"
            aria-label="Qué quieres rastrear"
            className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]"
          />
          <button
            type="submit"
            disabled={!consulta.trim() || rangoIncompleto || cargando}
            aria-describedby={(!consulta.trim() || rangoIncompleto) ? 'rastrear-falta' : undefined}
            className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white font-semibold rounded-xl px-5 py-3 disabled:opacity-50"
          >
            <Search className="h-4 w-4" aria-hidden="true" />
            Rastrear
          </button>
        </div>
        <Motivo id="rastrear-falta" cuando={!consulta.trim() || rangoIncompleto}>
          {!consulta.trim() ? 'Escribe un lote, un embarque o un número de parte para poder rastrear.' : 'Completa las dos fechas del rango, o deja las dos vacías.'}
        </Motivo>

        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-gray-600 mb-1">Desde (opcional)</span>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                   className="border border-gray-300 rounded-lg px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="block text-gray-600 mb-1">Hasta (opcional)</span>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                   className="border border-gray-300 rounded-lg px-3 py-2" />
          </label>
          {/* EL BOTÓN DESHABILITADO DICE POR QUÉ. Un control apagado sin
              explicación es una pared: quien lo ve no sabe si es su culpa. */}
          {rangoIncompleto && (
            <p className="text-sm text-amber-700">
              Pon las dos fechas o ninguna: con una sola no se puede acotar el periodo.
            </p>
          )}
        </div>
      </form>

      {error && (
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
          {error}
        </div>
      )}

      {cargando && <p className="text-sm text-gray-500">Buscando…</p>}

      {datos && !cargando && <Resultado
        datos={datos}
        puedeReportar={puedeReportar}
        onGenerar={generar}
        reporte={reporte}
      />}
    </div>
  );
}

// Se exportan `Resultado` y `Lote` además de la página: son las piezas que
// existen para DECIR algo —qué se encontró, por qué está vacío, qué es un
// embarque y qué no— y son las que se pueden probar de verdad con el
// renderizador de servidor, sin DOM ni sesión de mentira.
export function Resultado({ datos, puedeReportar, onGenerar, reporte }) {
  const necesitaPeriodo = NECESITAN_PERIODO.includes(datos.interpretado_como)
    && !datos.desde;

  if (datos.sin_sucursales) {
    return (
      <EstadoVacio
        icono={ShieldCheck}
        titulo="Todavía no tienes ninguna sucursal asignada"
        descripcion="Tu cuenta está acotada a sucursales concretas y no tiene ninguna. Pídele a quien administra los usuarios que te asigne la tuya; no es un problema del lote que buscas."
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-gray-700">
            <strong>{datos.explicacion || 'No reconocí ese dato.'}</strong>
          </p>
          {datos.hay && (
            <p className="text-xs text-gray-500 mt-0.5">
              Huella del contenido: <code className="font-mono">{datos.huella_contenido?.slice(0, 16)}…</code>
            </p>
          )}
        </div>
        {datos.hay && puedeReportar && (
          <button
            onClick={onGenerar}
            className="inline-flex items-center gap-2 border border-[var(--color-marca)] text-[var(--color-marca)] font-semibold rounded-xl px-4 py-2.5"
          >
            <FileDown className="h-4 w-4" aria-hidden="true" />
            Generar PDF para el auditor
          </button>
        )}
      </div>

      {reporte && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-900">
          <p className="font-semibold">{reporte.mensaje}</p>
          <p className="mt-1">
            <a href={reporte.url} className="underline font-semibold">Descargar el reporte {reporte.folio}</a>
            {' · '}Huella del contenido: <code className="font-mono">{reporte.huella_contenido?.slice(0, 16)}…</code>
          </p>
        </div>
      )}

      {datos.recortado && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
          La búsqueda tocó más lotes de los que caben en una pantalla y se recortó. Acota el periodo para verlos todos.
        </p>
      )}

      {necesitaPeriodo && (
        <EstadoVacio
          icono={PackageSearch}
          titulo="Ese dato sí existe, pero hace falta un periodo"
          descripcion="Reconocí lo que escribiste como número de parte o código de producto. Buscar así sin fechas devolvería todos los lotes de su historia; pon un periodo y vuelve a rastrear."
        />
      )}

      {!datos.hay && !necesitaPeriodo && (
        <EstadoVacio
          icono={PackageSearch}
          titulo="No encontré nada con ese dato"
          descripcion="Revisa que esté completo y sin espacios de más. Si es un número de parte o un código de producto, añade un periodo. Si es un lote de un proveedor, puede que todavía no se haya registrado al recibir."
        />
      )}

      {(datos.lotes || []).map((lote) => <Lote key={lote.id_lote} lote={lote} />)}
    </div>
  );
}

export function Lote({ lote }) {
  return (
    <section className="border border-gray-200 rounded-2xl overflow-hidden">
      <header className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">
          Lote {lote.codigo_lote} · {lote.sku}
        </h3>
        <p className="text-sm text-gray-600">{lote.producto}</p>
        {lote.numeros_de_parte.length > 0 && (
          <p className="text-xs text-gray-500 mt-1">
            {lote.numeros_de_parte.map((n) => (
              <span key={n.cliente} className="mr-3">
                {n.cliente}: <strong>{n.numero_parte_cliente}</strong>
                {n.nivel_ingenieria ? ` (nivel ${n.nivel_ingenieria})` : ''}
              </span>
            ))}
          </p>
        )}
      </header>

      <Bloque icono={Factory} titulo="De dónde vino"
              vacio="No se registró lote de proveedor para este lote. Si el material entró antes de llevar este control, la recepción sigue en el historial de movimientos.">
        {lote.origen.map((o, i) => (
          <li key={i} className="py-1.5">
            <strong>{o.proveedor}</strong> · lote <strong>{o.lote_proveedor}</strong> ·
            {' '}{o.cantidad} {' · '}
            {o.certificado
              ? <span>certificado: {o.certificado}</span>
              : <span className="text-amber-700">sin certificado adjunto</span>}
          </li>
        ))}
      </Bloque>

      <Bloque icono={Truck} titulo="Recepciones" vacio="Sin recepciones registradas con detalle.">
        {lote.recepciones.map((r, i) => (
          <li key={i} className="py-1.5">
            {cuando(r.recibido_en)} · orden {r.folio_orden} · {r.proveedor} ·
            {' '}{r.cantidad} · recibió {r.recibio || '—'}
          </li>
        ))}
      </Bloque>

      <Bloque icono={ArrowRightLeft} titulo="Qué se hizo con él" vacio="Sin movimientos registrados.">
        {lote.movimientos.map((m) => (
          <li key={m.id} className="py-1.5">
            {cuando(m.fecha)} · <strong>{m.tipo}</strong> · {m.cantidad} ·
            {' '}{m.ubicacion_origen || '—'} → {m.ubicacion_destino || '—'} · {m.quien || '—'}
          </li>
        ))}
      </Bloque>

      {lote.traslados.length > 0 && (
        <Bloque icono={ArrowRightLeft}
                titulo="Traslados entre sucursales (movimiento interno, no es un embarque)"
                vacio="">
          {lote.traslados.map((t, i) => (
            <li key={i} className="py-1.5">
              Envío {t.folio} · {cuando(t.enviado_en)} · {t.sucursal_origen || '—'} → {t.sucursal_destino || '—'} ·
              {' '}enviado {t.cantidad_enviada} · recibido {t.cantidad_recibida ?? '—'} · {t.estado}
            </li>
          ))}
        </Bloque>
      )}

      <Bloque icono={Truck} titulo="A dónde salió (embarques a cliente)"
              vacio="Este lote todavía no ha salido a ningún cliente.">
        {lote.embarques.map((e, i) => (
          <li key={i} className="py-1.5">
            Embarque {e.folio} · {cuando(e.creado_en)} · <strong>{e.cliente || 'Mostrador'}</strong> ·
            {' '}{e.cantidad}
            {e.numero_parte_cliente ? ` · su número de parte: ${e.numero_parte_cliente}` : ''}
            {' · '}{e.estado}
          </li>
        ))}
      </Bloque>

      {(lote.fecha_fabricacion || lote.fecha_caducidad) && (
        <p className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
          {lote.fecha_fabricacion && <>Fabricado: {dia(lote.fecha_fabricacion)}. </>}
          {lote.fecha_caducidad && <>Caduca: {dia(lote.fecha_caducidad)}.</>}
        </p>
      )}
    </section>
  );
}

// NUNCA UN BLOQUE EN BLANCO. Un hueco sin explicación se lee como un dato
// que falta; una frase que dice por qué está vacío se lee como lo que es.
function Bloque({ icono: Icono, titulo, vacio, children }) {
  const hijos = Array.isArray(children) ? children.filter(Boolean) : children;
  const vacioDeVerdad = !hijos || (Array.isArray(hijos) && hijos.length === 0);
  return (
    <div className="px-4 py-3 border-b border-gray-100 last:border-b-0">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-1">
        <Icono className="h-4 w-4 text-gray-500" aria-hidden="true" />
        {titulo}
      </h4>
      {vacioDeVerdad
        ? <p className="text-sm text-gray-500">{vacio}</p>
        : <ul className="text-sm text-gray-700 divide-y divide-gray-100">{hijos}</ul>}
    </div>
  );
}
